import React, { useState } from 'react';
import ApiClientTable from '../components/ApiClientTable';
import CreateApiClientModal from '../components/CreateApiClientModal';
import CreateWebhookModal from '../components/CreateWebhookModal';
import IntegrationRegistryPanel from '../components/IntegrationRegistryPanel';
import OpenPlatformFilterBar from '../components/OpenPlatformFilterBar';
import PlatformUsageSummaryCards from '../components/PlatformUsageSummaryCards';
import WebhookDeliveryDrawer from '../components/WebhookDeliveryDrawer';
import WebhookTable from '../components/WebhookTable';
import { useOpenPlatformData } from '../hooks/useOpenPlatformData';
import { normalizeErrorMessage } from '../lib/openPlatformFormatters';
import { AppShell, PageContainer } from '../../ui-system';

const OpenPlatformPage = () => {
  const {
    summary,
    clients,
    webhooks,
    deliveries,
    integrations,
    filters,
    setFilters,
    selectedWebhookId,
    setSelectedWebhookId,
    refreshAll,
    isLoading,
    error,
    actions,
  } = useOpenPlatformData();

  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [deliveryDrawerOpen, setDeliveryDrawerOpen] = useState(false);
  const [latestApiKey, setLatestApiKey] = useState('');

  const withErrorAlert = async (fn) => {
    try {
      const result = await fn();
      return result;
    } catch (err) {
      window.alert(normalizeErrorMessage(err));
      return null;
    }
  };

  const onCreateClient = async (payload) => {
    const result = await withErrorAlert(() => actions.createClientMutation.mutateAsync(payload));
    if (!result) return;
    setLatestApiKey(result.apiKey || '');
  };

  const onRotateClient = async (clientId) => {
    const result = await withErrorAlert(() => actions.rotateClientKeyMutation.mutateAsync(clientId));
    if (!result) return;
    setLatestApiKey(result.apiKey || '');
    setClientModalOpen(true);
  };

  const onRevokeClient = async (clientId) => {
    await withErrorAlert(() => actions.revokeClientMutation.mutateAsync(clientId));
  };

  const onCreateWebhook = async (payload) => {
    await withErrorAlert(() => actions.createWebhookMutation.mutateAsync(payload));
    setWebhookModalOpen(false);
  };

  const onTestWebhook = async (webhookId) => {
    await withErrorAlert(() => actions.testWebhookMutation.mutateAsync(webhookId));
  };

  const onDisableWebhook = async (webhookId) => {
    await withErrorAlert(() => actions.disableWebhookMutation.mutateAsync(webhookId));
  };

  const onViewDeliveries = (webhookId) => {
    setSelectedWebhookId(webhookId);
    setDeliveryDrawerOpen(true);
  };

  const onCreateIntegration = async (payload) => {
    await withErrorAlert(() => actions.createIntegrationMutation.mutateAsync(payload));
  };

  const onActivateIntegration = async (integrationId) => {
    await withErrorAlert(() => actions.activateIntegrationMutation.mutateAsync(integrationId));
  };

  const onDisableIntegration = async (integrationId) => {
    await withErrorAlert(() => actions.disableIntegrationMutation.mutateAsync(integrationId));
  };

  return (
    <AppShell>
      <PageContainer>
      <div className="w-full space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Open Platform API Layer</h1>
          <p className="text-sm text-slate-400">Manajemen API client, webhook, dan integrasi partner secara aman, versioned, dan tenant-scoped.</p>
        </div>

        <PlatformUsageSummaryCards summary={summary} />
        <OpenPlatformFilterBar filters={filters} onChange={setFilters} onRefresh={refreshAll} />

        {isLoading ? <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300">Memuat data open platform...</div> : null}
        {error ? <div className="rounded-lg border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">Gagal memuat data: {normalizeErrorMessage(error)}</div> : null}

        <ApiClientTable
          items={clients}
          onCreate={() => {
            setLatestApiKey('');
            setClientModalOpen(true);
          }}
          onRotateKey={onRotateClient}
          onRevoke={onRevokeClient}
        />

        <WebhookTable
          items={webhooks}
          onCreate={() => setWebhookModalOpen(true)}
          onTest={onTestWebhook}
          onDisable={onDisableWebhook}
          onViewDeliveries={onViewDeliveries}
        />

        <IntegrationRegistryPanel
          items={integrations}
          onCreate={onCreateIntegration}
          onActivate={onActivateIntegration}
          onDisable={onDisableIntegration}
        />

        <CreateApiClientModal
          open={clientModalOpen}
          onClose={() => setClientModalOpen(false)}
          onSubmit={onCreateClient}
          loading={actions.createClientMutation.isPending}
          createdApiKey={latestApiKey}
        />

        <CreateWebhookModal
          open={webhookModalOpen}
          clients={clients}
          onClose={() => setWebhookModalOpen(false)}
          onSubmit={onCreateWebhook}
          loading={actions.createWebhookMutation.isPending}
        />

        <WebhookDeliveryDrawer
          open={deliveryDrawerOpen}
          onClose={() => {
            setDeliveryDrawerOpen(false);
            setSelectedWebhookId(null);
          }}
          items={selectedWebhookId ? deliveries.filter((item) => item.webhookId === selectedWebhookId) : deliveries}
        />
      </div>
      </PageContainer>
    </AppShell>
  );
};

export default OpenPlatformPage;
